import { useState, useEffect } from 'react';
import { Calendar, Users, TrendingUp, Clock, Trash2 } from 'lucide-react';
import { getDashboardInsights, getEventLeads, updateLead, markAttendance, getActivities, getLeads, confirmFromWaitingList } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import { useToast } from '../hooks/useToast';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';

export const DashboardInsights = ({ currentView }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [leadsModalOpen, setLeadsModalOpen] = useState(false);
  const [selectedEventLeads, setSelectedEventLeads] = useState([]);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [healthDeclarations, setHealthDeclarations] = useState({});
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingAvailableLeads, setLoadingAvailableLeads] = useState(false);
  const [selectedLeadToAdd, setSelectedLeadToAdd] = useState('');
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(null);
  const { t, isRTL, language } = useLanguage();
  const toast = useToast();

  const fetchActivities = async () => {
    try {
      const data = await getActivities(true); // Only active activities
      setActivities(data || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  const fetchInsights = async (activityId = null) => {
    try {
      console.log('Fetching insights with activity_id:', activityId);
      const data = await getDashboardInsights(activityId);
      setInsights(data);
    } catch (error) {
      console.error('Failed to fetch dashboard insights:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch activities once on mount
  useEffect(() => {
    fetchActivities();
  }, []);

  // Fetch insights whenever currentView or activities change
  useEffect(() => {
    // Determine activity_id from currentView
    let activityId = null;
    
    if (currentView && currentView.filter_criteria && currentView.filter_criteria.activity) {
      // Find activity by name
      const activityKeyword = currentView.filter_criteria.activity;
      console.log('Looking for activity with keyword:', activityKeyword);
      console.log('Available activities:', activities);
      
      const matchedActivity = activities.find(a => 
        a.activity_name && a.activity_name.toLowerCase().includes(activityKeyword.toLowerCase())
      );
      
      if (matchedActivity) {
        activityId = matchedActivity.id;
        console.log('Matched activity ID:', activityId);
      } else {
        console.log('No matching activity found for keyword:', activityKeyword);
      }
    }

    // Only fetch if activities are loaded, or if there's no activity filter
    if (activities.length > 0 || !currentView?.filter_criteria?.activity) {
      fetchInsights(activityId);
    }
  }, [currentView, activities]);

  const handleViewLeads = async (event) => {
    try {
      setLoadingLeads(true);
      setSelectedEvent(event);
      setSelectedEventName(event.event_name);
      const leadsData = await getEventLeads(event.id);
      const leads = Array.isArray(leadsData) ? leadsData : [];
      setSelectedEventLeads(leads);

      // Initialize health declarations and attendance status from lead data
      const initialHealthDeclarations = {};
      const initialAttendanceStatus = {};
      leads.forEach(lead => {
        initialHealthDeclarations[lead.id] = lead.health_declaration === true;
        if (lead.attendance_status) {
          initialAttendanceStatus[lead.id] = lead.attendance_status;
        }
      });

      setHealthDeclarations(initialHealthDeclarations);
      setAttendanceStatus(initialAttendanceStatus);
      setLeadsModalOpen(true);
    } catch (error) {
      console.error('Failed to load event leads:', error);
      toast.error(t('failedToLoadLeads') || 'Failed to load event leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleUnassignLead = async (leadId) => {
    if (!confirm(t('confirmUnassignLead') || 'Are you sure you want to unassign this lead from the event?')) {
      return;
    }

    try {
      await updateLead(leadId, {
        event_id: null,
        action_date: null
      });

      toast.success(t('leadUnassigned') || 'Lead unassigned from event successfully');

      if (selectedEvent) {
        const leadsData = await getEventLeads(selectedEvent.id);
        setSelectedEventLeads(Array.isArray(leadsData) ? leadsData : []);
        fetchInsights(); // Refresh insights
      }
    } catch (error) {
      console.error('Failed to unassign lead:', error);
      
      // Check for specific error about attended leads
      if (error.response?.data?.message?.includes('attended')) {
        toast.error(t('cannotUnassignAttendedLead') || 'Cannot unassign lead who attended the event. Mark as absent first.');
      } else {
        toast.error(t('failedToUnassignLead') || 'Failed to unassign lead from event');
      }
    }
  };

  const handleAttendanceChange = async (leadId, attendance) => {
    try {
      const healthDeclaration = healthDeclarations[leadId] || null;

      setAttendanceStatus(prev => ({
        ...prev,
        [leadId]: attendance
      }));

      await markAttendance(leadId, attendance, healthDeclaration);
      toast.success(t('attendanceUpdated') || 'Attendance updated successfully');
    } catch (error) {
      console.error('Failed to update attendance:', error);
      toast.error(t('failedToUpdateAttendance') || 'Failed to update attendance');

      setAttendanceStatus(prev => {
        const newState = { ...prev };
        delete newState[leadId];
        return newState;
      });
    }
  };

  const handleHealthDeclarationChange = async (leadId, hasDeclaration) => {
    try {
      const newValue = hasDeclaration ? true : null;
      setHealthDeclarations(prev => ({
        ...prev,
        [leadId]: newValue
      }));

      const currentAttendance = attendanceStatus[leadId];
      await markAttendance(leadId, currentAttendance || null, newValue);
      toast.success(t('healthDeclarationUpdated') || 'Health declaration updated successfully');
    } catch (error) {
      console.error('Failed to update health declaration:', error);
      toast.error(t('failedToUpdateHealthDeclaration') || 'Failed to update health declaration');

      setHealthDeclarations(prev => ({
        ...prev,
        [leadId]: !hasDeclaration ? true : null
      }));
    }
  };

  const handleCloseLeadsModal = () => {
    setLeadsModalOpen(false);
    setSelectedEventLeads([]);
    setSelectedEventName('');
    setSelectedEvent(null);
    setHealthDeclarations({});
    setAttendanceStatus({});
  };

  const handleCopyParticipantsList = () => {
    if (!selectedEvent || selectedEventLeads.length === 0) {
      toast.error(t('noLeadsToCopy') || 'No leads to copy');
      return;
    }

    // Format event time in Hebrew format
    const eventDate = new Date(selectedEvent.event_date);
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');
    const timeString = `שעה ${hours}:${minutes}:`;

    // Build the participants list
    const participantsList = selectedEventLeads
      .map(lead => `* ${lead.field_data?.full_name || 'Unnamed Lead'}`)
      .join('\n');

    // Combine time and list
    const fullText = `${timeString}\n${participantsList}`;

    // Copy to clipboard
    navigator.clipboard.writeText(fullText).then(() => {
      toast.success(t('copiedToClipboard') || 'Copied to clipboard');
    }).catch((err) => {
      console.error('Failed to copy:', err);
      toast.error(t('failedToCopy') || 'Failed to copy to clipboard');
    });
  };

  const handleConfirmFromWaitingList = async (leadId) => {
    if (!selectedEvent?.id) return;

    try {
      await confirmFromWaitingList(leadId, selectedEvent.id);
      toast.success(t('leadConfirmedFromWaitingList') || 'Lead confirmed from waiting list successfully');

      // Refresh the leads list
      const leadsData = await getEventLeads(selectedEvent.id);
      setSelectedEventLeads(Array.isArray(leadsData) ? leadsData : []);

      // Refresh insights to update counts
      fetchInsights();
    } catch (error) {
      console.error('Failed to confirm lead from waiting list:', error);

      if (error.response?.data?.error?.code === 'EVENT_AT_CAPACITY') {
        toast.error(t('eventAtCapacity') || 'Event has reached capacity');
      } else {
        toast.error(t('failedToConfirmLead') || 'Failed to confirm lead from waiting list');
      }
    }
  };

  const handleOpenAddLeadModal = async () => {
    setAddLeadModalOpen(true);
    setLoadingAvailableLeads(true);
    try {
      // Fetch all leads
      const response = await getLeads({ limit: 1000 });
      setAvailableLeads(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error(t('failedToLoadLeads') || 'Failed to load leads');
      setAvailableLeads([]);
    } finally {
      setLoadingAvailableLeads(false);
    }
  };

  const handleCloseAddLeadModal = () => {
    setAddLeadModalOpen(false);
    setSelectedLeadToAdd('');
    setLeadSearchQuery('');
    setAvailableLeads([]);
  };

  const handleAddLeadToEvent = async () => {
    if (!selectedLeadToAdd || !selectedEvent) {
      toast.error(t('pleaseSelectLead') || 'Please select a lead');
      return;
    }

    try {
      // Update the lead to assign it to the event
      await updateLead(selectedLeadToAdd, {
        event_id: selectedEvent.id,
        action_date: selectedEvent.event_date
      });

      toast.success(t('leadAddedToEvent') || 'Lead added to event successfully');
      handleCloseAddLeadModal();

      // Refresh the leads list for the current event
      if (selectedEvent) {
        const leadsData = await getEventLeads(selectedEvent.id);
        setSelectedEventLeads(Array.isArray(leadsData) ? leadsData : []);
      }

      // Refresh insights to update assigned count
      fetchInsights();
    } catch (error) {
      console.error('Failed to add lead to event:', error);
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'EVENT_AT_CAPACITY') {
        toast.error(t('eventAtCapacity') || 'Event is at full capacity');
      } else if (errorCode === 'LEAD_NOT_FOUND') {
        toast.error(t('leadNotFound') || 'Lead not found');
      } else if (errorCode === 'EVENT_NOT_FOUND') {
        toast.error(t('eventNotFound') || 'Event not found');
      } else {
        toast.error(t('failedToAddLead') || 'Failed to add lead to event');
      }
    }
  };

  const handlePhoneClick = (leadId, phone) => {
    if (!phone) return;
    setPhoneMenuOpen(phoneMenuOpen === leadId ? null : leadId);
  };

  const handleCopyPhone = (phone) => {
    if (!phone) return;
    navigator.clipboard.writeText(phone).then(() => {
      toast.success(t('copiedToClipboard') || 'Copied to clipboard');
      setPhoneMenuOpen(null);
    }).catch((err) => {
      console.error('Failed to copy:', err);
      toast.error(t('failedToCopy') || 'Failed to copy to clipboard');
    });
  };

  const handleOpenWhatsApp = (phone) => {
    if (!phone) return;
    // Remove any non-digit characters except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
    setPhoneMenuOpen(null);
  };

  if (loading || !insights) {
    return null;
  }

  const { upcomingEvents, totalLeads, newLeadsToday, conversionRate } = insights;

  return (
    <div className="mb-6">
      {/* Upcoming Events Section */}
      {upcomingEvents && upcomingEvents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {t('upcomingEvents') || 'Upcoming Events'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingEvents.slice(0, 3).map((event) => {
              const capacityPercentage = event.max_capacity
                ? (event.assigned_leads_count / event.max_capacity) * 100
                : 0;
              const isFull = capacityPercentage >= 100;
              const isAlmostFull = capacityPercentage >= 80;

              return (
                <div
                  key={event.id}
                  onClick={() => handleViewLeads(event)}
                  className={`border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md ${
                    isFull ? 'border-red-300 bg-red-50 hover:bg-red-100' :
                    isAlmostFull ? 'border-yellow-300 bg-yellow-50 hover:bg-yellow-100' :
                    'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {event.event_name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(event.event_date).toLocaleDateString('he-IL', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        isFull ? 'bg-red-100 text-red-700' :
                        isAlmostFull ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {event.assigned_leads_count}{event.max_capacity > 0 ? `/${event.max_capacity}` : ''}
                      </span>
                      {event.waiting_list_count > 0 && (
                        <span className="text-xs font-semibold px-2 py-1 rounded bg-purple-100 text-purple-700">
                          {t('waitingList')}: {event.waiting_list_count}
                        </span>
                      )}
                    </div>
                  </div>

                  {event.max_capacity && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>{t('capacity', 'תפוסה')}</span>
                        <span>{Math.round(capacityPercentage)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            isFull ? 'bg-red-500' :
                            isAlmostFull ? 'bg-yellow-500' :
                            'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {event.location && (
                    <p className="text-xs text-gray-500 mt-2 truncate">
                      📍 {event.location}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('totalLeads', 'סה"כ לידים')}</p>
              <p className="text-xl font-bold text-gray-900">{totalLeads || 0}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('newToday', 'חדשים היום')}</p>
              <p className="text-xl font-bold text-gray-900">{newLeadsToday || 0}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('conversionRate', 'שיעור המרה')}</p>
              <p className="text-xl font-bold text-gray-900">{conversionRate || 0}%</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('upcomingEvents') || 'Upcoming Events'}</p>
              <p className="text-xl font-bold text-gray-900">{upcomingEvents?.length || 0}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Leads Modal */}
      <Modal
        isOpen={leadsModalOpen}
        onClose={handleCloseLeadsModal}
        title={`${t('assignedLeads') || 'Assigned Leads'} - ${selectedEventName}`}
      >
        <div className="mb-4">
          <Button onClick={handleOpenAddLeadModal} variant="primary">
            {t('addLead') || 'Add Lead'}
          </Button>
        </div>
        {loadingLeads ? (
          <div className="text-center py-8">{t('loading')}</div>
        ) : selectedEventLeads.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noLeadsAssigned') || 'No leads assigned to this event'}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Statistics Summary */}
            {(() => {
              const confirmedLeads = selectedEventLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) !== 'waiting_list'
              );
              const waitingListLeads = selectedEventLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) === 'waiting_list'
              );
              const totalAssigned = confirmedLeads.length;
              const capacity = selectedEvent?.capacity || 0;
              const attended = confirmedLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) === 'attended'
              ).length;
              const didntArrive = confirmedLeads.filter(lead => {
                const status = attendanceStatus[lead.id] || lead.attendance_status;
                return status === 'absent' || status === 'no_show';
              }).length;
              const missingHealthDeclaration = confirmedLeads.filter(lead =>
                !healthDeclarations[lead.id] && !lead.health_declaration
              ).length;

              return (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-xs text-blue-600 font-medium mb-1">
                        {t('assignedOfCapacity') || 'Assigned / Capacity'}
                      </div>
                      <div className="text-2xl font-bold text-blue-900">
                        {totalAssigned}{capacity > 0 ? ` / ${capacity}` : ''}
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="text-xs text-green-600 font-medium mb-1">
                        {t('attended') || 'Attended'}
                      </div>
                      <div className="text-2xl font-bold text-green-900">{attended}</div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="text-xs text-red-600 font-medium mb-1">
                        {t('didntArrive') || "Didn't Arrive"}
                      </div>
                      <div className="text-2xl font-bold text-red-900">{didntArrive}</div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="text-xs text-orange-600 font-medium mb-1">
                        {t('missingHealthDeclaration') || 'Missing Health Statement'}
                      </div>
                      <div className="text-2xl font-bold text-orange-900">{missingHealthDeclaration}</div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="text-xs text-purple-600 font-medium mb-1">
                        {t('waitingList') || 'Waiting List'}
                      </div>
                      <div className="text-2xl font-bold text-purple-900">{waitingListLeads.length}</div>
                    </div>
                  </div>

                  {/* Confirmed Leads Section */}
                  {confirmedLeads.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {t('confirmedAttendees') || 'Confirmed Attendees'}
                      </h4>
                    </div>
                  )}
                </>
              );
            })()}
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('name') || 'Name'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('phone') || 'Phone'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('group') || 'Group'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('attendance') || 'Attendance'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('healthDeclaration') || 'Health Declaration'}
                      </th>
                      <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                        {t('actions') || 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedEventLeads.filter(lead =>
                      (attendanceStatus[lead.id] || lead.attendance_status) !== 'waiting_list'
                    ).map((lead) => {
                      const currentAttendanceStatus = attendanceStatus[lead.id] || lead.attendance_status;
                      const isAttended = currentAttendanceStatus === 'attended';
                      const isAbsent = currentAttendanceStatus === 'absent';
                      const hasHealthDeclaration = healthDeclarations[lead.id] === true;

                      return (
                        <tr key={lead.id}>
                          <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            <div className="flex items-center gap-2">
                              <span>{lead.field_data?.full_name || '-'}</span>
                              {!hasHealthDeclaration && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                                  title={t('missingHealthDeclaration') || 'Missing health declaration'}
                                >
                                  ⚠️
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            {lead.field_data?.phone || '-'}
                          </td>
                          <td className={`px-4 py-3 text-sm text-gray-600 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            {lead.group_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-3">
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  key={`attended-${lead.id}`}
                                  type="radio"
                                  name={`attendance-${lead.id}`}
                                  value="attended"
                                  className="w-5 h-5 text-green-600 border-2 border-gray-300 focus:ring-green-500 focus:ring-2"
                                  checked={isAttended}
                                  onChange={() => handleAttendanceChange(lead.id, 'attended')}
                                  style={{ accentColor: '#16a34a' }}
                                />
                                <span className={`text-sm font-medium ${isAttended ? 'text-green-700' : 'text-gray-600'}`}>
                                  {t('attended') || 'הגיע'}
                                </span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  key={`absent-${lead.id}`}
                                  type="radio"
                                  name={`attendance-${lead.id}`}
                                  value="absent"
                                  className="w-5 h-5 text-red-600 border-2 border-gray-300 focus:ring-red-500 focus:ring-2"
                                  checked={isAbsent}
                                  onChange={() => handleAttendanceChange(lead.id, 'absent')}
                                  style={{ accentColor: '#dc2626' }}
                                />
                                <span className={`text-sm font-medium ${isAbsent ? 'text-red-700' : 'text-gray-600'}`}>
                                  {t('absent') || 'לא הגיע'}
                                </span>
                              </label>
                              {(isAttended || isAbsent) && (
                                <button
                                  onClick={() => handleAttendanceChange(lead.id, 'pending')}
                                  className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                                  title={t('resetAttendance') || 'Reset attendance'}
                                >
                                  <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                    <path d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center">
                              <button
                                onClick={() => handleHealthDeclarationChange(lead.id, !hasHealthDeclaration)}
                                className={`flex items-center justify-center w-8 h-8 rounded-full transition-all cursor-pointer hover:scale-110 ${
                                  hasHealthDeclaration 
                                    ? 'bg-green-100 border-2 border-green-500 hover:bg-green-200' 
                                    : 'bg-gray-100 border-2 border-gray-300 hover:bg-gray-200'
                                }`}
                                title={hasHealthDeclaration ? (t('healthDeclarationReceived') || 'Health declaration received') : (t('healthDeclarationNotReceived') || 'No health declaration')}
                              >
                                {hasHealthDeclaration ? (
                                  <svg className="w-5 h-5 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                    <path d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                    <path d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            <button
                              onClick={() => handleUnassignLead(lead.id)}
                              className="text-red-600 hover:text-red-900"
                              title={t('unassign') || 'Unassign'}
                            >
                              <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {(() => {
                const confirmedLeads = selectedEventLeads.filter(lead =>
                  (attendanceStatus[lead.id] || lead.attendance_status) !== 'waiting_list'
                );
                const pendingLeads = confirmedLeads.filter(lead => {
                  const status = attendanceStatus[lead.id] || lead.attendance_status;
                  return !status || status === 'pending';
                });
                const markedLeads = confirmedLeads.filter(lead => {
                  const status = attendanceStatus[lead.id] || lead.attendance_status;
                  return status === 'attended' || status === 'absent' || status === 'no_show';
                });

                const renderMobileCard = (lead) => {
                  const currentAttendanceStatus = attendanceStatus[lead.id] || lead.attendance_status;
                  const isAttended = currentAttendanceStatus === 'attended';
                  const isAbsent = currentAttendanceStatus === 'absent';
                  const hasHealthDeclaration = healthDeclarations[lead.id] === true;

                  return (
                    <div key={lead.id} className="bg-gray-50 rounded-lg p-3 border relative">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 text-right flex-wrap">
                            <h4 className="text-sm font-semibold text-gray-900">{lead.field_data?.full_name || '-'}</h4>
                            <div className="relative flex-shrink-0">
                              <button
                                onClick={() => handlePhoneClick(lead.id, lead.field_data?.phone)}
                                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                              >
                                {lead.field_data?.phone || '-'}
                              </button>
                              {phoneMenuOpen === lead.id && lead.field_data?.phone && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleCopyPhone(lead.field_data.phone)}
                                      className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('copyToClipboard')}
                                    </button>
                                    <button
                                      onClick={() => handleOpenWhatsApp(lead.field_data.phone)}
                                      className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('openInWhatsApp')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {!hasHealthDeclaration && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0"
                                title={t('missingHealthDeclaration') || 'Missing health declaration'}
                              >
                                ⚠️
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleHealthDeclarationChange(lead.id, !hasHealthDeclaration)}
                            className={`flex items-center justify-center w-5 h-5 rounded-full transition-all ${
                              hasHealthDeclaration
                                ? 'bg-green-100 border border-green-500'
                                : 'bg-gray-100 border border-gray-300'
                            }`}
                            title={hasHealthDeclaration ? t('healthDeclarationReceived') : t('healthDeclarationNotReceived')}
                          >
                            {hasHealthDeclaration ? (
                              <svg className="w-3 h-3 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleUnassignLead(lead.id)}
                            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                            title={t('unassign') || 'Unassign'}
                          >
                            <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-200">
                        <button
                          onClick={() => handleAttendanceChange(lead.id, 'attended')}
                          className={`flex-1 py-1.5 px-3 rounded text-sm font-medium transition-colors ${
                            isAttended
                              ? 'bg-green-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {t('attended') || 'הגיע'}
                        </button>
                        <button
                          onClick={() => handleAttendanceChange(lead.id, 'absent')}
                          className={`flex-1 py-1.5 px-3 rounded text-sm font-medium transition-colors ${
                            isAbsent
                              ? 'bg-red-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {t('absent') || 'לא הגיע'}
                        </button>
                        {(isAttended || isAbsent) && (
                          <button
                            onClick={() => handleAttendanceChange(lead.id, 'pending')}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title={t('resetAttendance') || 'Reset attendance'}
                          >
                            <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    {/* Pending Attendance Section */}
                    {pendingLeads.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2 px-1">
                          {t('pendingAttendance') || 'טרם סומן'} ({pendingLeads.length})
                        </h5>
                        <div className="space-y-2">
                          {pendingLeads.map(renderMobileCard)}
                        </div>
                      </div>
                    )}

                    {/* Marked Attendance Section */}
                    {markedLeads.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2 px-1">
                          {t('markedAttendance') || 'סומנו'} ({markedLeads.length})
                        </h5>
                        <div className="space-y-2">
                          {markedLeads.map(renderMobileCard)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Waiting List Section */}
            {(() => {
              const waitingListLeads = selectedEventLeads.filter(lead =>
                (attendanceStatus[lead.id] || lead.attendance_status) === 'waiting_list'
              );

              if (waitingListLeads.length === 0) return null;

              return (
                <div className="mt-8 pt-6 border-t-2 border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-4">
                    {t('waitingList') || 'Waiting List'} ({waitingListLeads.length})
                  </h4>

                  {/* Desktop Waiting List Table */}
                  <div className="hidden md:block">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-purple-50">
                          <tr>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('name') || 'Name'}
                            </th>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('phone') || 'Phone'}
                            </th>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('group') || 'Group'}
                            </th>
                            <th className={`px-4 py-3 text-xs font-medium text-purple-700 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {t('actions') || 'Actions'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {waitingListLeads.map((lead) => (
                            <tr key={lead.id} className="bg-purple-50/30">
                              <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                {lead.field_data?.full_name || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                {lead.field_data?.phone || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm text-gray-600 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                {lead.group_name || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleConfirmFromWaitingList(lead.id)}
                                    className="px-3 py-1 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors"
                                  >
                                    {t('confirm') || 'Confirm'}
                                  </button>
                                  <button
                                    onClick={() => handleUnassignLead(lead.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Waiting List Cards */}
                  <div className="md:hidden space-y-3">
                    {waitingListLeads.map((lead) => (
                      <div key={lead.id} className="bg-purple-50/50 rounded-lg p-4 border border-purple-200">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-medium text-gray-900">{lead.field_data?.full_name || '-'}</div>
                            <div className="text-sm text-gray-600 mt-1">{lead.field_data?.phone || '-'}</div>
                            {lead.group_name && (
                              <div className="text-xs text-gray-500 mt-1">
                                {t('group')}: {lead.group_name}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 pt-3 border-t border-purple-200">
                          <button
                            onClick={() => handleConfirmFromWaitingList(lead.id)}
                            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors"
                          >
                            {t('confirm') || 'Confirm'}
                          </button>
                          <button
                            onClick={() => handleUnassignLead(lead.id)}
                            className="px-3 py-2 text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-between items-center pt-4 border-t">
              <Button 
                variant="primary" 
                onClick={handleCopyParticipantsList}
                disabled={selectedEventLeads.length === 0}
              >
                <svg className="w-4 h-4 mr-2" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('copyParticipantsList') || 'Copy Participants List'}
              </Button>
              <Button variant="secondary" onClick={handleCloseLeadsModal}>
                {t('close')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Lead to Event Modal */}
      <Modal
        isOpen={addLeadModalOpen}
        onClose={handleCloseAddLeadModal}
        title={`${t('addLeadToEvent') || 'Add Lead to Event'} - ${selectedEvent?.event_name || ''}`}
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {t('addLeadToEventInfo') || 'Select a lead to add to this event.'}
            </p>
          </div>

          {loadingAvailableLeads ? (
            <div className="text-center py-8">{t('loading')}</div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('searchLead') || 'Search Lead'}
                </label>
                <Input
                  type="text"
                  value={leadSearchQuery}
                  onChange={(e) => setLeadSearchQuery(e.target.value)}
                  placeholder={t('searchByNameOrPhone') || 'Search by name or phone...'}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('selectLead') || 'Select Lead'}
                </label>
                <select
                  value={selectedLeadToAdd}
                  onChange={(e) => setSelectedLeadToAdd(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('selectLead') || 'Select Lead'}</option>
                  {availableLeads
                    .filter((lead) => {
                      if (!leadSearchQuery) return true;
                      const fullName = lead.field_data?.full_name?.toLowerCase() || '';
                      const phone = lead.field_data?.phone?.toLowerCase() || '';
                      const query = leadSearchQuery.toLowerCase();
                      return fullName.includes(query) || phone.includes(query);
                    })
                    .map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.field_data?.full_name || 'Unnamed'} {lead.field_data?.phone ? `- ${lead.field_data.phone}` : ''}
                      </option>
                    ))}
                </select>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseAddLeadModal}>
              {t('cancel')}
            </Button>
            <Button type="button" onClick={handleAddLeadToEvent} disabled={!selectedLeadToAdd}>
              {t('add') || 'Add'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
